import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getConfig } from '@edx/frontend-platform';
import { sendPageEvent, sendTrackEvent } from '@edx/frontend-platform/analytics';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import Skeleton from 'react-loading-skeleton';
import { Link } from 'react-router-dom';

// Import background image and logo
import authBG from '../assets/authBG.jpg';
import logo from '../assets/logo.png';

import { useT } from '../context/LanguageContext';

import {
  InstitutionLogistration,
  RedirectLogistration,
  ThirdPartyAuthAlert,
} from '../common-components';
import AccountActivationMessage from './AccountActivationMessage';
import { getThirdPartyAuthContext } from '../common-components/data/actions';
import { thirdPartyAuthContextSelector } from '../common-components/data/selectors';
import EnterpriseSSO from '../common-components/EnterpriseSSO';
import { PENDING_STATE, REGISTER_PAGE, RESET_PAGE } from '../data/constants';
import {
  getActivationStatus,
  getAllPossibleQueryParams,
  getTpaHint,
  getTpaProvider,
  updatePathWithQueryParams,
} from '../data/utils';
import ResetPasswordSuccess from '../reset-password/ResetPasswordSuccess';
import { backupLoginFormBegin, dismissPasswordResetBanner, loginRequest } from './data/actions';
import { INVALID_FORM, TPA_AUTHENTICATION_FAILURE } from './data/constants';
import LoginFailureMessage from './LoginFailure';

const LoginPage = ({
  institutionLogin,
  handleInstitutionLogin,
}) => {
  const dispatch = useDispatch();
  const backupFormState = useCallback((data) => dispatch(backupLoginFormBegin(data)), [dispatch]);
  const getTPADataFromBackend = useCallback(() => dispatch(getThirdPartyAuthContext()), [dispatch]);
  const {
    backedUpFormData,
    loginErrorCode,
    loginErrorContext,
    loginResult,
    shouldBackupState,
    showResetPasswordSuccessBanner,
    submitState,
    thirdPartyAuthContext,
    thirdPartyAuthApiStatus,
  } = useSelector((state) => ({
    backedUpFormData: state.login.loginFormData,
    loginErrorCode: state.login.loginErrorCode,
    loginErrorContext: state.login.loginErrorContext,
    loginResult: state.login.loginResult,
    shouldBackupState: state.login.shouldBackupState,
    showResetPasswordSuccessBanner: state.login.showResetPasswordSuccessBanner,
    submitState: state.login.submitState,
    thirdPartyAuthContext: thirdPartyAuthContextSelector(state),
    thirdPartyAuthApiStatus: state.commonComponents.thirdPartyAuthApiStatus,
  }));
  const {
    providers,
    currentProvider,
    secondaryProviders,
    finishAuthUrl,
    platformName,
    errorMessage: thirdPartyErrorMessage,
  } = thirdPartyAuthContext;
  const { t, toggleLang, isArabic } = useT();
  const activationMsgType = getActivationStatus();
  const queryParams = useMemo(() => getAllPossibleQueryParams(), []);

  const [formFields, setFormFields] = useState({ ...backedUpFormData.formFields });
  const [errorCode, setErrorCode] = useState({
    type: '',
    count: 0,
    context: {},
  });
  const [errors, setErrors] = useState({ ...backedUpFormData.errors });
  const [rememberMe, setRememberMe] = useState(false);
  const tpaHint = getTpaHint();

  useEffect(() => {
    sendPageEvent('login_and_registration', 'login');
  }, []);

  useEffect(() => {
    const payload = { ...queryParams };
    if (tpaHint) {
      payload.tpa_hint = tpaHint;
    }
    getTPADataFromBackend(payload);
  }, [queryParams, tpaHint, getTPADataFromBackend]);

  useEffect(() => {
    if (shouldBackupState) {
      backupFormState({
        formFields: { ...formFields },
        errors: { ...errors },
      });
    }
  }, [backupFormState, shouldBackupState, formFields, errors]);

  useEffect(() => {
    if (loginErrorCode) {
      setErrorCode(prevState => ({
        type: loginErrorCode,
        count: prevState.count + 1,
        context: { ...loginErrorContext },
      }));
    }
  }, [loginErrorCode, loginErrorContext]);

  useEffect(() => {
    if (thirdPartyErrorMessage) {
      setErrorCode((prevState) => ({
        type: TPA_AUTHENTICATION_FAILURE,
        count: prevState.count + 1,
        context: {
          errorMessage: thirdPartyErrorMessage,
        },
      }));
    }
  }, [thirdPartyErrorMessage]);


  const validateFormFields = (payload) => {
    const { emailOrUsername, password } = payload;
    const fieldErrors = { ...errors };

    if (emailOrUsername === '') {
      fieldErrors.emailOrUsername = t('email.validation.message');
    } else if (emailOrUsername.length < 2) {
      fieldErrors.emailOrUsername = t('username.or.email.format.validation.less.chars.message');
    }
    if (password === '') {
      fieldErrors.password = t('password.validation.message');
    }

    return { ...fieldErrors };
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (showResetPasswordSuccessBanner) {
      dispatch(dismissPasswordResetBanner());
    }

    const formData = { ...formFields };
    const validationErrors = validateFormFields(formData);
    if (validationErrors.emailOrUsername || validationErrors.password) {
      setErrors({ ...validationErrors });
      setErrorCode(prevState => ({
        type: INVALID_FORM,
        count: prevState.count + 1,
        context: {},
      }));
      return;
    }

    const payload = {
      email: formData.emailOrUsername,
      password: formData.password,
      ...queryParams,
    };
    dispatch(loginRequest(payload));
  };

  const handleOnChange = (event) => {
    const { name, value } = event.target;
    setFormFields(prevState => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleOnFocus = (event) => {
    const { name } = event.target;
    setErrors(prevErrors => ({
      ...prevErrors,
      [name]: '',
    }));
  };

  const trackForgotPasswordLinkClick = () => {
    sendTrackEvent('edx.bi.password-reset_form.toggled', { category: 'user-engagement' });
  };

  const { provider, skipHintedLogin } = getTpaProvider(tpaHint, providers, secondaryProviders);

  if (tpaHint) {
    if (thirdPartyAuthApiStatus === PENDING_STATE) {
      return <Skeleton height={36} />;
    }

    if (skipHintedLogin) {
      window.location.href = getConfig().LMS_BASE_URL + provider.loginUrl;
      return null;
    }

    if (provider) {
      return <EnterpriseSSO provider={provider} />;
    }
  }

  if (institutionLogin) {
    return (
      <InstitutionLogistration
        secondaryProviders={secondaryProviders}
        headingTitle={t('institution.login.page.title')}
      />
    );
  }

  // Microsoft Icon SVG with solid color
  const MicrosoftIcon = () => (
    <svg className="microsoft-icon" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="9" height="9" fill="#008EC3" />
      <rect x="11" y="1" width="9" height="9" fill="#008EC3" />
      <rect x="1" y="11" width="9" height="9" fill="#008EC3" />
      <rect x="11" y="11" width="9" height="9" fill="#008EC3" />
    </svg>
  );

  // Google Icon SVG
  const GoogleIcon = () => (
    <svg className="google-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );

  // Azure Icon SVG
  const AzureIcon = () => (
    <svg className="azure-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M13.05 4.24L6.56 18.05L2 18.41L9.11 4.24H13.05ZM13.75 5.88L22 18.41H17.04L13.75 5.88ZM7.09 8.46L11.62 14.38L11.25 18.12L3.32 18.41L7.09 8.46Z"
        fill="#0089D6"
      />
    </svg>
  );

  return (
    <>
      <Helmet>
        <title>{t('login.page.title', { siteName: getConfig().SITE_NAME })}</title>
      </Helmet>
      <RedirectLogistration
        success={loginResult.success}
        redirectUrl={loginResult.redirectUrl}
        finishAuthUrl={finishAuthUrl}
      />

      <div
        className="ksu-login-page"
        style={{
          backgroundImage: `url(${authBG})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        {/* Language Switcher */}
        <div className="ksu-lang-switcher">
          <button type="button" className="lang-btn" onClick={toggleLang}>
            {isArabic ? 'EN' : 'العربية'}
          </button>
        </div>

        {/* Header with Logo */}
        <header className="ksu-header">
          <div className="ksu-logo-container">
            <a href="/">
              <img
                src={logo}
                alt="Platform Logo"
                className="ksu-logo"
              />
            </a>
          </div>
        </header>

        {/* Main Login Card */}
        <main className="ksu-main-content">
          <div className="ksu-login-card">
            <h1 className="ksu-login-title">
              {t('logistration.sign.in')}
            </h1>

            <div className="ksu-register-link">
              {t('logistration.register.link.prefix')}{' '}
              <Link to={updatePathWithQueryParams(REGISTER_PAGE)}>
                {t('logistration.register.link.text')}
              </Link>
            </div>

            {/* Error Messages */}
            <LoginFailureMessage
              errorCode={errorCode.type}
              errorCount={errorCode.count}
              context={errorCode.context}
            />
            <ThirdPartyAuthAlert
              currentProvider={currentProvider}
              platformName={platformName}
            />
            <AccountActivationMessage messageType={activationMsgType} />
            {showResetPasswordSuccessBanner && <ResetPasswordSuccess />}

            {/* Login Form */}
            <form className="ksu-form" onSubmit={handleSubmit}>
              <div className="ksu-form-group">
                <label htmlFor="emailOrUsername" className="ksu-label">
                  {t('login.user.identity.label')}
                </label>
                <input
                  id="emailOrUsername"
                  name="emailOrUsername"
                  type="text"
                  className={`ksu-input ${errors.emailOrUsername ? 'is-invalid' : ''}`}
                  placeholder="email@domain.com"
                  value={formFields.emailOrUsername || ''}
                  onChange={handleOnChange}
                  onFocus={handleOnFocus}
                  autoComplete="email"
                />
                <p className="ksu-helper-text">
                  {t('login.email.help.text', { platformName: getConfig().SITE_NAME })}
                </p>
                {errors.emailOrUsername && (
                  <p className="ksu-error-text">{errors.emailOrUsername}</p>
                )}
              </div>

              <div className="ksu-form-group">
                <label htmlFor="password" className="ksu-label">
                  {t('login.password.label')}
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  className={`ksu-input ${errors.password ? 'is-invalid' : ''}`}
                  placeholder={t('login.password.label')}
                  value={formFields.password || ''}
                  onChange={handleOnChange}
                  onFocus={handleOnFocus}
                  autoComplete="current-password"
                />
                {errors.password && (
                  <p className="ksu-error-text">{errors.password}</p>
                )}
              </div>

              <button
                type="submit"
                className="ksu-login-btn"
                disabled={submitState === 'pending'}
              >
                {submitState === 'pending' ? '...' : t('sign.in.button')}
              </button>

              <div className="ksu-footer-links">
                <div className="ksu-remember-me">
                  <input
                    type="checkbox"
                    id="rememberMe"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <label htmlFor="rememberMe">
                    {t('logistration.remember.me')}
                  </label>
                </div>

                <div className="ksu-forgot-password">
                  {t('forgot.password.prefix')}{' '}
                  <Link
                    to={updatePathWithQueryParams(RESET_PAGE)}
                    onClick={trackForgotPasswordLinkClick}
                  >
                    {t('forgot.password.link.text')}
                  </Link>
                </div>
              </div>
            </form>
          </div>
        </main>

        {/* Social Login Section */}
        {(providers?.length > 0 || secondaryProviders?.length > 0) && (
          <section className="ksu-social-login">
            <p className="ksu-social-title">
              {t('logistration.social.login.title')}
            </p>
            {/* Primary Providers (e.g., KSU SSO) */}
            {providers?.length > 0 && (
              <div className="ksu-social-buttons">
                {providers.map((provider) => (
                  <button
                    key={provider.id}
                    type="button"
                    className="ksu-social-btn"
                    onClick={() => {
                      window.location.href = getConfig().LMS_BASE_URL + provider.loginUrl;
                    }}
                  >
                    {provider.id.includes('azuread') && <MicrosoftIcon />}
                    {provider.name}
                  </button>
                ))}
              </div>
            )}
            {/* Secondary Providers (e.g., Google, Azure) */}
            {secondaryProviders?.length > 0 && (
              <div className="ksu-social-buttons ksu-social-buttons-secondary">
                {secondaryProviders.map((provider) => {
                  const isGoogle =provider.name.toLowerCase().includes('google');
                  const isAzure = provider.name.toLowerCase().includes('azure');
                  
                  return (
                    <button
                      key={provider.id}
                      type="button"
                      className={`ksu-social-btn ${isGoogle ? 'ksu-social-btn-google' : ''} ${isAzure ? 'ksu-social-btn-azure' : ''}`}
                      onClick={() => {
                        window.location.href = getConfig().LMS_BASE_URL + provider.loginUrl;
                      }}
                    >
                      {isGoogle && <GoogleIcon />}
                      {isAzure && <AzureIcon />}
                      {!isGoogle && !isAzure && <MicrosoftIcon />}
                      {isArabic ? `تسجيل الدخول بواسطة ${provider.name}` : `Login with ${provider.name}`}
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        )}
      </div>
    </>
  );
};

LoginPage.propTypes = {
  institutionLogin: PropTypes.bool.isRequired,
  handleInstitutionLogin: PropTypes.func.isRequired,
};

// Wrapper component - LoginPage handles locale internally via useT()
const LoginPageWrapper = (props) => <LoginPage {...props} />;

LoginPageWrapper.propTypes = {
  institutionLogin: PropTypes.bool.isRequired,
  handleInstitutionLogin: PropTypes.func.isRequired,
};

export default LoginPageWrapper;
